<template>
  <div v-if="user" class="my-account-component">
    <h2 class="my-account-head-caption">My Account</h2>
    <div class="my-account-wrapper">
      <form
              novalidate
              @submit.stop.prevent="saveBasicInfo"
              @reset.stop.prevent="cancelBasicInfoEditing"
              :class="['basic-info-form', {'basic-info-editable': isBasicInfoEditable}]">
        <div class="basic-info-header">
          <h3>Basic info</h3>
          <md-button @click="toggleBasicInfoEditableMode" :disabled="isSaving || isSecurityInfoEditable"  class="md-icon-button">
            <md-icon>edit</md-icon>
            <md-tooltip md-direction="right" md-delay="400">Edit</md-tooltip>
          </md-button>
        </div>

        <md-input-container class="basic-info-input-readonly">
          <label>Email</label>
          <md-input v-model="user.email" :disabled="isBasicInfoEditable" readonly></md-input>
        </md-input-container>

        <p v-if="isBasicInfoEditable" class="basic-info-email-message">
          <small>Contact <a href="mailto:support@widgetbrain.com">Widget Brain Support</a> to update your email address.</small>
        </p>

        <md-input-container v-if="!isBasicInfoEditable" class="basic-info-input-readonly">
          <label>Name</label>
          <md-input v-model="user.name" readonly></md-input>
        </md-input-container>

        <md-input-container v-if="isBasicInfoEditable" :class="{'md-input-invalid': !isNewNameValid}">
          <label>Name</label>
          <md-input v-model="newName" @input="validateFullName" :debounce="500" @input.native="isNewNameValid = false"></md-input>
          <span class="md-error">{{ validationMessageOfNewName }}</span>
        </md-input-container>

        <div v-if="isBasicInfoEditable" class="basic-info-controls">
          <md-button type="submit" class="md-primary md-raised" :disabled="!isNewNameValid">Save</md-button>
          <md-button type="reset" class="md-default">Cancel</md-button>
        </div>
      </form>


      <form
              novalidate
              @submit.stop.prevent="saveNewPassword"
              @reset.stop.prevent="isSecurityInfoEditable=false"
              class="security-info-form">
        <div class="security-info-header">
          <h3>Security</h3>
        </div>

        <div>
          <md-button
                  @click="togglePasswordChangeMode"
                  v-if="!isSecurityInfoEditable"
                  :disabled="isBasicInfoEditable || isSaving"
                  class="md-primary security-info-reset-password-btn">
            Reset password
          </md-button>
        </div>

        <div v-if="isSecurityInfoEditable" class="security-info-passwords-wrapper">
          <md-input-container :class="{'md-input-invalid': !!validationMessageOfOldPassword}">
            <label>Old password</label>
            <md-input v-model="oldPassword" type="password" required></md-input>
            <span class="md-error">{{ validationMessageOfOldPassword }}</span>
          </md-input-container>
          <md-input-container :class="{'md-input-invalid': !isNewPasswordValid}">
            <label>New password</label>
            <md-input v-model="newPassword" @input="validateNewPassword" :debounce="600" @input.native="isNewPasswordValid = false" type="password" required></md-input>
            <span class="md-error">{{ validationMessageOfNewPassword }}</span>
          </md-input-container>
          <md-input-container :class="{'md-input-invalid': !arePasswordsSame}">
            <label>Confirm new password</label>
            <md-input v-model="confirmPassword" @input="confirmNewPassword" :debounce="600" @input.native="arePasswordsSame = false" type="password" required></md-input>
            <span class="md-error">{{ validationMessageOfConfirmePassword }}</span>
          </md-input-container>
        </div>

        <div v-if="isSecurityInfoEditable" class="security-info-controls">
          <md-button type="submit" :disabled="!isReadyToSaveNewPassword" class="md-primary md-raised">Save</md-button>
          <md-button type="reset" class="md-default">Cancel</md-button>
        </div>
          <div>
              <md-button
                      @click="resetCredentials"
                      class="md-primary security-info-reset-password-btn">
                  Reset auth0 credentials
              </md-button>
          </div>
      </form>


      <div class="login-history-wrapper">
        <h3 class="login-history-header">Login history</h3>

        <md-table v-if="sortedLogs.length">
          <md-table-header>
            <md-table-row>
              <md-table-head class="login-history-log-success">Success</md-table-head>
              <md-table-head class="login-history-log-action">Action</md-table-head>
              <md-table-head class="login-history-log-ip">IP</md-table-head>
              <md-table-head class="login-history-log-date">When</md-table-head>
            </md-table-row>
          </md-table-header>

          <md-table-body>
            <md-table-row v-for="log in sortedLogs" :key="log.id">
              <md-table-cell class="login-history-log-success">
                <md-icon v-if="log.success" class="login-history-success-icon">check</md-icon>
                <md-icon v-else class="login-history-failure-icon">warning</md-icon>
                <md-tooltip md-direction="left">{{ log.success ? 'Success' : 'Failure' }}</md-tooltip>
              </md-table-cell>
              <md-table-cell class="login-history-log-action">{{ log.action }}</md-table-cell>
              <md-table-cell class="login-history-log-ip">{{ log.IP }}</md-table-cell>
              <md-table-cell class="login-history-log-date">
                {{ log.ago }}
                <md-tooltip md-direction="right">{{ log.date.toLocaleString() }}</md-tooltip>
              </md-table-cell>
            </md-table-row>
          </md-table-body>
        </md-table>

        <div v-else class="login-history-stub">
          <md-spinner v-if="!noLogsFound" md-indeterminate></md-spinner>
          <p v-else class="login-history-nothing-to-show">Nothing to show yet...</p>
        </div>
      </div>
    </div>
    <md-dialog ref="credentialsDialog">
      <md-dialog-title>
        Your new credentials
      </md-dialog-title>
      <md-dialog-content>
        <b>Client ID:</b> {{ clientId }}<br>
        <b>Client Secret:</b> {{ clientSecret }}<br>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button @click="closeCredentialsDialog">Ok</md-button>
      </md-dialog-actions>
    </md-dialog>
  </div>
  <div v-else class="my-account-loading">
    <md-spinner :md-size="150" md-indeterminate></md-spinner>
  </div>
</template>


<script>
    import { mapGetters } from 'vuex';
    import managedata from '../helpers/managedata';
    import timeAgo from 'time-ago';

    export default {
        name: 'MyAccount',
        methods: {
            // Basic Info methods
            toggleBasicInfoEditableMode: function() {
                if(!this.isBasicInfoEditable) {
                    this.newName = this.user.name;
                }

                this.isBasicInfoEditable = !this.isBasicInfoEditable;
            },
            validateFullName: function() {
                if (this.newName === '') {
                    this.isNewNameValid = false;
                    this.validationMessageOfNewName = 'Full name should not be empty';
                    return;
                }
                managedata.validateFullName(this.newName)
                    .then((res) => {
                        const result = res.body.result;
                        this.isNewNameValid = result.valid;
                        this.validationMessageOfNewName = result.valid ? '' : 'Full name is not valid';
                    })
                    .catch((err) => {
                        this.isNewNameValid = false;
                        console.log(err);
                    })
            },
            saveBasicInfo: function() {
                this.isBasicInfoEditable = false;
                if (this.newName === this.user.name) return;
                this.isSaving = true;
                this.$store.dispatch("updateUser", { id: this.user.id, name: this.newName.trim() })
                    .finally(() => {
                        this.isSaving = false;
                    })
            },
            cancelBasicInfoEditing: function() {
                this.isBasicInfoEditable = false;
            },

            // Security Info methods
            togglePasswordChangeMode: function() {
                this.oldPassword = '';
                this.newPassword = '';
                this.confirmPassword = '';
                this.validationMessageOfNewPassword = '';
                this.validationMessageOfConfirmePassword = '';
                this.isNewPasswordValid = true;
                this.arePasswordsSame = true;
                this.isSecurityInfoEditable = !this.isSecurityInfoEditable;
            },
            validateNewPassword: function() {
                if(this.newPassword === this.oldPassword) {
                    this.isNewPasswordValid = false;
                    this.validationMessageOfNewPassword = 'The new and old passwords should be different';
                    return;
                }
                managedata.validatePassword(this.newPassword)
                    .then((res) => {
                        const result = res.body.result;
                        this.isNewPasswordValid = result.valid;
                        this.validationMessageOfNewPassword = result.requirements[0] || '';
                        this.confirmNewPassword();
                    })
                    .catch((err) => {
                        this.isNewPasswordValid = false;
                        console.log(err);
                    })
            },
            confirmNewPassword: function() {
                if(this.confirmPassword === '') return;
                if(this.newPassword === this.confirmPassword) {
                    this.arePasswordsSame = true;
                    this.validationMessageOfConfirmePassword = '';
                } else {
                    this.arePasswordsSame = false;
                    this.validationMessageOfConfirmePassword = 'Passwords do not match';
                }
            },
            saveNewPassword: function() {
                this.isSecurityInfoEditable = false;
                this.isSaving = true;
                this.$store.dispatch("changePassword",
                    { userId: this.user.id,
                        oldPassword: this.oldPassword,
                        newPassword: this.newPassword
                    })
                    .catch((err) => {
                        if (err.body && err.body.error && err.body.error.message) {
                            this.$store.commit({
                                type: 'SET_MESSAGE',
                                message: err.body.error.message
                            });
                        }
                    })
                    .finally(() => {
                        this.isSaving = false;
                    })
            },
            resetCredentials() {
                this.$store.dispatch('resetAuth0Credentials')
                    .then(res => {
                        this.$refs.credentialsDialog.open();
                        this.clientId = res.body.clientId;
                        this.clientSecret = res.body.clientSecret;
                    })
                    .catch(err => {
                        console.log(err);
                    })
            },
            closeCredentialsDialog() {
                this.$refs.credentialsDialog.close();
                this.clientId = '';
                this.clientSecret = '';
            }
        },
        mounted: function() {
            managedata.getUserLogs()
                .then((res) => {
                    const logs = res.body;
                    // if there is no login/logout logs then consider we have nothing to show
                    this.noLogsFound = !logs.some(
                        (log) => ~log.route.toLowerCase().indexOf('login') || ~log.route.toLowerCase().indexOf('logout')
                    );
                    this.logs = logs;
                })
                .catch((err) => {
                    this.noLogsFound = true;
                    console.log(err);
                });
        },
        computed: {
            ...mapGetters([
                'user'
            ]),
            isReadyToSaveNewPassword: function() {
                return (
                    this.oldPassword
                    && this.newPassword
                    && this.confirmPassword
                    && this.isNewPasswordValid
                    && this.arePasswordsSame
                );
            },
            sortedLogs: function() {
                return this.logs.filter((log) => {
                    // exclude everything except login/logout attempts
                    return ~log.route.toLowerCase().indexOf('login') || ~log.route.toLowerCase().indexOf('logout');
                }).map((log) => {
                    let action;
                    if (~log.route.toLowerCase().indexOf('login')) {
                        action = 'Login';
                    }
                    if (~log.route.toLowerCase().indexOf('logout')) {
                        action = 'Logout';
                    }

                    const logDate = new Date(log.logTime);

                    const newLog = {
                        id: log.id,
                        success: log.httpstatus.toString()[0] === '2', // 2xx http statuses are successful
                        action: action,
                        IP: log.ipAddress,
                        ago: timeAgo.ago(logDate),
                        date: logDate
                    };

                    return newLog;
                }).sort((a, b) => {
                    return b.date - a.date;
                });
            }
        },
        data() {
            return {
                isSaving: false,
                logs: [],

                // Basic Info data
                isBasicInfoEditable: false,
                isNewNameValid: true,
                newName: '',
                validationMessageOfNewName: '',


                // Security Info data
                isSecurityInfoEditable: false,
                isNewPasswordValid: true,
                arePasswordsSame: true,

                oldPassword: '',
                newPassword: '',
                confirmPassword: '',

                validationMessageOfOldPassword: '',
                validationMessageOfNewPassword: '',
                validationMessageOfConfirmePassword: '',

                noLogsFound: false,

                clientId: '',
                clientSecret: '',
            }
        },
        watch: {
            oldPassword(val) {
                managedata.validateOldPassword(val)
                    .then(resp => {
                        this.validationMessageOfOldPassword = resp.body.message;
                    })
            }
        }
    }

    /*
    logsMock:
    [ {
        "logType": "INFO",
        "methodType": "GET",
        "httpstatus": 200,
        "logTime": "2018-05-03T07:23:45.659Z",
        "route": "/api/WBUsers/login",
        "ipAddress": "52.58.29.20",
        "userId": 488,
        "appId": 10,
        "token": "orAvZFXTix8Nm3I356R7mTPIzPfjQDYGpaQsquXJHLaIfFdtX82RExImudzk2lnJ",
        "id": "5aeab903cc43417d12c2b21a",
        "environment": "test"
      },
      {
        "logType": "INFO",
        "methodType": "GET",
        "httpstatus": 200,
        "logTime": "2018-05-03T07:24:45.659Z",
        "route": "/api/WBUsers/logout",
        "ipAddress": "52.58.29.20",
        "userId": 488,
        "appId": 10,
        "token": "orAvZFXTix8Nm3I356R7mTPIzPfjQDYGpaQsquXJHLaIfFdtX82RExImudzk2lnJ",
        "id": "5aeab903ca43417d12c2e21a",
        "environment": "test"
      },
      {
        "logType": "INFO",
        "methodType": "GET",
        "httpstatus": 200,
        "logTime": "2018-05-04T07:25:45.659Z",
        "route": "/api/WBUsers/login",
        "ipAddress": "52.58.29.20",
        "userId": 488,
        "appId": 10,
        "token": "orAvZFXTix8Nm3I356R7mTPIzPfjQDYGpaQsquXJHLaIfFdtX82RExImudzk2lnJ",
        "id": "5aecb903cc43417d12c2e21a",
        "environment": "test"
      },
      {
        "logType": "ERROR",
        "methodType": "GET",
        "httpstatus": 500,
        "logTime": "2018-05-03T07:39:45.659Z",
        "route": "/api/WBUsers/login",
        "ipAddress": "52.58.29.20",
        "userId": 488,
        "appId": 10,
        "id": "3aeab903cc43417d12c2e21a",
        "environment": "test"
      },
      {
        "logType": "ERROR",
        "methodType": "GET",
        "httpstatus": 500,
        "logTime": "2018-05-04T09:44:45.659Z",
        "route": "/api/WBUsers/login",
        "ipAddress": "52.58.29.20",
        "userId": 488,
        "appId": 10,
        "id": "1aeab903cc43417d12c2e21a",
        "environment": "test"
      }
    ]
    */
</script>


<style scoped>
  @import url('https://fonts.googleapis.com/css?family=Roboto');


  p {
    margin: 0;
  }


  .my-account-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 600px;
  }
  .my-account-component {
    font-family: 'Roboto', sans-serif;
    color: #333;
  }
  .my-account-wrapper {
    width: 500px;
    margin: auto;
  }
  .my-account-head-caption {
    padding: 20px;
    text-align: center;
  }


  .basic-info-form {
    margin: 0 0 60px 0;
  }
  .basic-info-form.basic-info-editable{
    margin: 0 0 30px 0;
  }
  .basic-info-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 25px;
  }
  .basic-info-header, .security-info-header {
    border-bottom: 1px solid #ccc;
  }
  .basic-info-header h3 {
    margin: 0;
  }
  .basic-info-email-message {
    margin: -20px 0 15px 0;
    opacity: 0.75;
    transition: opacity 0.2s;
  }
  .basic-info-email-message:hover {
    opacity: 1;
  }
  .basic-info-input-readonly input {
    cursor: auto !important;
  }
  .basic-info-input-readonly::after {
    height: 1px !important;
    background-color: rgba(0, 0, 0, 0.12) !important;
  }
  .basic-info-input-readonly label {
    color: rgba(0, 0, 0, 0.54) !important;
  }
  .basic-info-controls, .security-info-controls {
    display: flex;
    justify-content: flex-end;
  }


  .security-info-header h3 {
    margin-bottom: 12px;
  }
  .security-info-passwords-wrapper {
    margin-top: 25px;
  }
  .security-info-reset-password-btn {
    width: 97%;
    margin-top: 15px;
    line-height: 50px;
  }

  .login-history-wrapper {
    margin: 30px 0 100px 0;
  }
  .login-history-header {
    margin-bottom: 30px;
    font-weight: 500;
  }
  .login-history-success-icon {
    color: #2195f3;
  }
  .login-history-failure-icon {
    color: #f35e21;
  }
  .login-history-log-success {
    width: 20%;
  }
  .login-history-log-action {
    width: 20%;
  }
  .login-history-log-ip {
    width: 25%;
  }
  .login-history-log-date {
    width: 35%;
  }

  .login-history-stub {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 70px;
  }
  .login-history-nothing-to-show {
    font-size: 30px;
    color: rgba(0,0,0,0.2);
  }
</style>

<style>
  .login-history-wrapper table {
    table-layout: fixed;
  }
  .login-history-wrapper table .md-table-head-text {
    overflow: visible;
  }
  .login-history-wrapper table .md-table-cell.login-history-log-date .md-table-cell-container {
    padding: 0 32px 0 24px;
  }
</style>



// WEBPACK FOOTER //
// MyAccount.vue?5bc41c3e